/**
 * Mulch & Stuff Landscape Material Project Planner
 * Phase 3 — Material, weight, bag, and related-material rules
 *
 * This module is independent of DOM rendering and does not alter the
 * authoritative cubic-yard or geometry calculations.
 */
(function (root, factory) {
  'use strict';

  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.MSProjectPlannerRules = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const STATUS = Object.freeze({
    UNAVAILABLE: 'unavailable',
    INCOMPLETE: 'incomplete',
    INVALID: 'invalid',
    VALID: 'valid'
  });

  const MATERIAL_AVAILABILITY = Object.freeze({
    AVAILABLE: 'available',
    BULK_AVAILABLE: 'bulk-available',
    BAG_ONLY: 'bag-only',
    TEMPORARILY_UNAVAILABLE: 'temporarily-unavailable',
    NOT_OFFERED_IN_PLANNER: 'not-offered-in-planner'
  });

  // Ignore only floating-point arithmetic noise at an exact whole unit.
  // This is not an allowance or a purchasing safety factor.
  function ceilPurchaseQuantity(value) {
    const nearest = Math.round(value);
    return nearest > 0 && Math.abs(value - nearest) <= Number.EPSILON * Math.max(1, Math.abs(value)) * 4
      ? nearest : Math.ceil(value);
  }

  // Purchase capability and availability status are independent configuration.
  function purchaseMethods(material) {
    if (!material) return [];
    return [material.bulkCapable ? 'bulk' : null, material.bagCapable ? 'bags' : null].filter(Boolean);
  }

  function resolvePurchaseMethod(material, selected) {
    const methods = purchaseMethods(material);
    return methods.length === 1 ? methods[0] : methods.includes(selected) ? selected : null;
  }

  function deriveSpacedStaples(linearFeet, spacingFeet) {
    const length = normalizePositiveFinite(linearFeet);
    const spacing = normalizePositiveFinite(spacingFeet);
    if (length === null || spacing === null) return {status: STATUS.INVALID};
    // Two staples at the start and at each interval or final edge.
    const stapleQuantity = 2 * (ceilPurchaseQuantity(length / spacing) + 1);
    return Number.isSafeInteger(stapleQuantity)
      ? {status: STATUS.VALID, stapleQuantity} : {status: STATUS.INVALID};
  }

  function derivePartialRectangle(length, width, lengthSides, widthSides, squareSides) {
    if (!(length > 0 && width > 0)) return {status: STATUS.INVALID};
    const equal = Math.abs(length - width) < 1e-9;
    if (equal) {
      const count = Number(squareSides);
      if (![1, 2, 3].includes(count)) return {status: STATUS.INCOMPLETE};
      return {status: STATUS.VALID, boundaryFeet: length * count};
    }
    if (lengthSides === null || lengthSides === undefined || lengthSides === '' || widthSides === null || widthSides === undefined || widthSides === '') {
      return {status: STATUS.INCOMPLETE};
    }
    const a = Number(lengthSides), b = Number(widthSides);
    if (![0, 1, 2].includes(a) || ![0, 1, 2].includes(b) || a + b === 0) return {status: STATUS.INVALID};
    return {status: STATUS.VALID, boundaryFeet: length * a + width * b};
  }

  function knownEdgingPerimeter(facts) {
    if (!facts) return null;
    return facts.totalFeet || (facts.insideFeet > 0 && facts.outsideFeet > 0 ? facts.insideFeet + facts.outsideFeet : null);
  }

  function deriveAdditionalMaterials(inputs, measurement, dimensions, ancillary) {
    const result = {fabric: 0, staples: 0, sticks: 0, purchasedFeet: 0, connectors: 0, corners: 0};
    const count = Number(inputs.identicalAreaMultiplier);
    if (!measurement || measurement.status !== STATUS.VALID || !Number.isInteger(count) || count < 1) return result;
    const related = inputs.relatedMaterials || {};
    const fabric = related.fabric || {}, staples = related.staples || {}, edge = related.alphaEdge || {};
    if (fabric.include === true && [10, 15, 20].includes(Number(fabric.allowancePercent))) {
      const requirement = deriveFabricRequirement(measurement.squareFeet, fabric.allowancePercent, ancillary.fabric.widthFeet, 1);
      if (requirement.status === STATUS.VALID) {
        result.fabric = requirement.linearFeet * count;
        if (staples.include === true) {
          const spaced = deriveSpacedStaples(requirement.linearFeet,
            staples.spacingChoice === 'custom' ? staples.customSpacingFeet : staples.spacingChoice);
          if (spaced.status === STATUS.VALID) result.staples = spaced.stapleQuantity * count;
        }
      }
    }
    if (edge.include !== true) return result;
    let boundary = null;
    if (edge.extent === 'entire') boundary = knownEdgingPerimeter(measurement.perimeterFacts);
    if (edge.extent === 'partial' && dimensions) {
      const partial = derivePartialRectangle(dimensions.length, dimensions.width,
        edge.lengthSides, edge.widthSides, edge.squareSides);
      if (partial.status === STATUS.VALID) boundary = partial.boundaryFeet;
    }
    if (!knownEdgingPerimeter(measurement.perimeterFacts) ||
        (edge.extent === 'partial' && !dimensions) ||
        (edge.extent === 'entire' && !boundary)) {
      const manual = normalizePositiveWholeNumber(edge.manualFeet);
      if (manual.status === STATUS.VALID) boundary = manual.value;
    }
    if (boundary > 0) {
      const purchase = deriveProjectEdgePurchase(boundary, ancillary.alphaEdge.stickLengthFeet);
      if (purchase.status === STATUS.VALID) {
        result.sticks = purchase.stickQuantity * count;
        result.purchasedFeet = purchase.purchasedFeet * count;
        result.connectors = result.sticks;
        if (dimensions && edge.cornersInclude === true && [1, 2, 3, 4].includes(Number(edge.ninetyDegreeConnectorsPerArea))) {
          result.corners = Number(edge.ninetyDegreeConnectorsPerArea) * count;
        }
      }
    }
    return result;
  }

  function isNonemptyString(value) {
    return (
      typeof value === 'string' &&
      value.trim().length > 0
    );
  }

  function isAuthorizedMaterialAvailability(value) {
    return Object.values(
      MATERIAL_AVAILABILITY
    ).includes(value);
  }

  function normalizePositiveFinite(value) {
    const numeric = Number(value);

    if (!Number.isFinite(numeric) || numeric <= 0) {
      return null;
    }

    return numeric;
  }

  function validateMaterialConfig(material) {
    if (
      !material ||
      typeof material !== 'object' ||
      Array.isArray(material)
    ) {
      return {
        status: STATUS.INVALID,
        errors: ['material-object']
      };
    }

    const errors = [];

    if (!isNonemptyString(material.id)) {
      errors.push('id');
    }

    if (!isNonemptyString(material.label)) {
      errors.push('label');
    }

    if (
      material.subtitle !== undefined &&
      material.subtitle !== null &&
      !isNonemptyString(material.subtitle)
    ) {
      errors.push('subtitle');
    }

    if (
      !Number.isInteger(material.displayOrder) ||
      material.displayOrder < 0
    ) {
      errors.push('displayOrder');
    }

    if (
      material.category !== undefined &&
      material.category !== null &&
      !isNonemptyString(material.category)
    ) {
      errors.push('category');
    }

    if (typeof material.plannerVisible !== 'boolean') {
      errors.push('plannerVisible');
    }

    if (
      !isAuthorizedMaterialAvailability(
        material.availability
      )
    ) {
      errors.push('availability');
    }

    if (typeof material.bulkCapable !== 'boolean') {
      errors.push('bulkCapable');
    }

    if (typeof material.bagCapable !== 'boolean') {
      errors.push('bagCapable');
    }

    const weight =
      normalizeWeightConfig(material.weight);

    if (weight.status === STATUS.INVALID) {
      errors.push('weight');
    }

    const bag =
      normalizeBagConfig(material.bagConversion);

    if (
      material.bagCapable === true &&
      bag.status !== STATUS.VALID
    ) {
      errors.push('bagConversion');
    }

    if (
      material.bagCapable === false &&
      bag.status === STATUS.INVALID
    ) {
      errors.push('bagConversion');
    }

    if (
      !Array.isArray(material.depthPresets) ||
      material.depthPresets.length === 0
    ) {
      errors.push('depthPresets');
    } else {
      const depthPresetsValid =
        material.depthPresets.every(
          function (preset) {
            return (
              Number.isInteger(preset) &&
              preset > 0
            );
          }
        );

      if (!depthPresetsValid) {
        errors.push('depthPresets');
      }
    }

    if (
      material.productInfoUrl !== undefined &&
      material.productInfoUrl !== null &&
      !isNonemptyString(material.productInfoUrl)
    ) {
      errors.push('productInfoUrl');
    }

    if (errors.length > 0) {
      return {
        status: STATUS.INVALID,
        errors
      };
    }

    return {
      status: STATUS.VALID
    };
  }

  function normalizeWeightConfig(weightConfig) {
    if (
      weightConfig === null ||
      weightConfig === undefined ||
      weightConfig === false
    ) {
      return {
        status: STATUS.UNAVAILABLE
      };
    }

    if (
      typeof weightConfig !== 'object' ||
      Array.isArray(weightConfig)
    ) {
      return {
        status: STATUS.INVALID
      };
    }

    if (weightConfig.type === 'single') {
      const poundsPerCubicYard =
        normalizePositiveFinite(
          weightConfig.poundsPerCubicYard
        );

      if (poundsPerCubicYard === null) {
        return {
          status: STATUS.INVALID
        };
      }

      return {
        status: STATUS.VALID,
        type: 'single',
        poundsPerCubicYard
      };
    }

    if (weightConfig.type === 'range') {
      const minimumPoundsPerCubicYard =
        normalizePositiveFinite(
          weightConfig.minimumPoundsPerCubicYard
        );
      const maximumPoundsPerCubicYard =
        normalizePositiveFinite(
          weightConfig.maximumPoundsPerCubicYard
        );

      if (
        minimumPoundsPerCubicYard === null ||
        maximumPoundsPerCubicYard === null ||
        minimumPoundsPerCubicYard >
          maximumPoundsPerCubicYard
      ) {
        return {
          status: STATUS.INVALID
        };
      }

      return {
        status: STATUS.VALID,
        type: 'range',
        minimumPoundsPerCubicYard,
        maximumPoundsPerCubicYard
      };
    }

    if (weightConfig.type === 'none') {
      return {
        status: STATUS.UNAVAILABLE
      };
    }

    return {
      status: STATUS.INVALID
    };
  }

  function deriveEstimatedWeight(
    cubicYards,
    weightConfig
  ) {
    const normalizedCubicYards =
      normalizePositiveFinite(cubicYards);
    const normalizedWeight =
      normalizeWeightConfig(weightConfig);

    if (normalizedCubicYards === null) {
      return {
        status: STATUS.INVALID
      };
    }

    if (
      normalizedWeight.status !==
      STATUS.VALID
    ) {
      return {
        status: normalizedWeight.status
      };
    }

    if (normalizedWeight.type === 'single') {
      return {
        status: STATUS.VALID,
        type: 'single',
        pounds:
          normalizedCubicYards *
          normalizedWeight.poundsPerCubicYard,
        poundsPerCubicYard:
          normalizedWeight.poundsPerCubicYard
      };
    }

    return {
      status: STATUS.VALID,
      type: 'range',
      minimumPounds:
        normalizedCubicYards *
        normalizedWeight.minimumPoundsPerCubicYard,
      maximumPounds:
        normalizedCubicYards *
        normalizedWeight.maximumPoundsPerCubicYard,
      minimumPoundsPerCubicYard:
        normalizedWeight.minimumPoundsPerCubicYard,
      maximumPoundsPerCubicYard:
        normalizedWeight.maximumPoundsPerCubicYard
    };
  }

  function normalizeBagConfig(bagConfig) {
    if (
      bagConfig === null ||
      bagConfig === undefined ||
      bagConfig === false
    ) {
      return {
        status: STATUS.UNAVAILABLE
      };
    }

    if (
      typeof bagConfig !== 'object' ||
      Array.isArray(bagConfig)
    ) {
      return {
        status: STATUS.INVALID
      };
    }

    if (bagConfig.type === 'none') {
      return {
        status: STATUS.UNAVAILABLE
      };
    }

    if (bagConfig.type !== 'volume') {
      return {
        status: STATUS.INVALID
      };
    }

    const cubicFeetPerBag =
      normalizePositiveFinite(
        bagConfig.cubicFeetPerBag
      );

    if (cubicFeetPerBag === null) {
      return {
        status: STATUS.INVALID
      };
    }

    return {
      status: STATUS.VALID,
      type: 'volume',
      cubicFeetPerBag
    };
  }

  function deriveBagQuantity(
    cubicYards,
    bagConfig
  ) {
    const normalizedCubicYards =
      normalizePositiveFinite(cubicYards);
    const normalizedBag =
      normalizeBagConfig(bagConfig);

    if (normalizedCubicYards === null) {
      return {
        status: STATUS.INVALID
      };
    }

    if (
      normalizedBag.status !==
      STATUS.VALID
    ) {
      return {
        status: normalizedBag.status
      };
    }

    const exactBagQuantity =
      normalizedCubicYards *
      27 /
      normalizedBag.cubicFeetPerBag;

    return {
      status: STATUS.VALID,
      type: 'volume',
      cubicFeetPerBag:
        normalizedBag.cubicFeetPerBag,
      exactBagQuantity,
      bagQuantity:
        ceilPurchaseQuantity(exactBagQuantity)
    };
  }

  function normalizeNonnegativeWholeNumber(value) {
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ''
    ) {
      return {
        status: STATUS.UNAVAILABLE
      };
    }

    const numeric = Number(value);

    if (
      !Number.isFinite(numeric) ||
      numeric < 0 ||
      !Number.isInteger(numeric)
    ) {
      return {
        status: STATUS.INVALID
      };
    }

    return {
      status: STATUS.VALID,
      value: numeric
    };
  }

  function deriveFabricRequirement(
    squareFeet,
    allowancePercent,
    fabricWidthFeet,
    multiplier
  ) {
    const normalizedSquareFeet =
      normalizePositiveFinite(squareFeet);
    const normalizedAllowance =
      normalizeNonnegativeWholeNumber(
        allowancePercent
      );
    const normalizedWidth =
      normalizePositiveFinite(
        fabricWidthFeet
      );
    const normalizedMultiplier =
      normalizePositiveFinite(
        multiplier
      );

    if (
      normalizedSquareFeet === null ||
      normalizedWidth === null ||
      normalizedMultiplier === null ||
      !Number.isInteger(normalizedMultiplier)
    ) {
      return {
        status: STATUS.INVALID
      };
    }

    if (
      normalizedAllowance.status !==
      STATUS.VALID
    ) {
      return {
        status: normalizedAllowance.status
      };
    }

    const adjustedSquareFeet =
      normalizedSquareFeet *
      (1 + normalizedAllowance.value / 100);

    const exactLinearFeet =
      adjustedSquareFeet *
      normalizedMultiplier /
      normalizedWidth;

    return {
      status: STATUS.VALID,
      allowancePercent:
        normalizedAllowance.value,
      fabricWidthFeet:
        normalizedWidth,
      adjustedSquareFeet,
      multiplier:
        normalizedMultiplier,
      exactLinearFeet,
      linearFeet:
        ceilPurchaseQuantity(exactLinearFeet)
    };
  }

  function normalizePositiveWholeNumber(value) {
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ''
    ) {
      return {
        status: STATUS.UNAVAILABLE
      };
    }

    const numeric = Number(value);

    if (
      !Number.isFinite(numeric) ||
      numeric <= 0 ||
      !Number.isInteger(numeric)
    ) {
      return {
        status: STATUS.INVALID
      };
    }

    return {
      status: STATUS.VALID,
      value: numeric
    };
  }

  function deriveStapleRequirement(
    exactFabricLinearFeet,
    staplesPerEightLinearFeet
  ) {
    const normalizedFabricLinearFeet =
      normalizePositiveFinite(
        exactFabricLinearFeet
      );

    const normalizedRate =
      normalizePositiveWholeNumber(
        staplesPerEightLinearFeet
      );

    if (
      normalizedFabricLinearFeet === null
    ) {
      return {
        status: STATUS.INVALID
      };
    }

    if (
      normalizedRate.status !==
      STATUS.VALID
    ) {
      return {
        status: normalizedRate.status
      };
    }

    const exactStapleQuantity =
      normalizedFabricLinearFeet *
      normalizedRate.value /
      8;

    return {
      status: STATUS.VALID,
      staplesPerEightLinearFeet:
        normalizedRate.value,
      exactFabricLinearFeet:
        normalizedFabricLinearFeet,
      exactStapleQuantity,
      stapleQuantity:
        Math.ceil(exactStapleQuantity)
    };
  }

  function resolveEdgeBoundary(
    measurementMethod,
    perimeterFacts,
    boundaryBasis,
    manualBoundaryFeet
  ) {
    const facts =
      perimeterFacts &&
      typeof perimeterFacts === 'object'
        ? perimeterFacts
        : {};

    if (boundaryBasis === 'manual') {
      const manualFeet =
        normalizePositiveFinite(
          manualBoundaryFeet
        );

      if (manualFeet === null) {
        return {
          status: STATUS.INVALID
        };
      }

      return {
        status: STATUS.VALID,
        boundaryBasis: 'manual',
        boundaryFeet: manualFeet
      };
    }

    if (
      measurementMethod === 'rectangle' ||
      measurementMethod === 'round-area'
    ) {
      if (boundaryBasis !== 'entire') {
        return {
          status: STATUS.INVALID
        };
      }

      const totalFeet =
        normalizePositiveFinite(
          facts.totalFeet
        );

      if (totalFeet === null) {
        return {
          status: STATUS.INVALID
        };
      }

      return {
        status: STATUS.VALID,
        boundaryBasis: 'entire',
        boundaryFeet: totalFeet
      };
    }

    if (measurementMethod === 'round-border') {
      let boundaryFeet = null;

      if (boundaryBasis === 'inside') {
        boundaryFeet =
          normalizePositiveFinite(
            facts.insideFeet
          );
      } else if (boundaryBasis === 'outside') {
        boundaryFeet =
          normalizePositiveFinite(
            facts.outsideFeet
          );
      } else if (boundaryBasis === 'both') {
        const insideFeet =
          normalizePositiveFinite(
            facts.insideFeet
          );
        const outsideFeet =
          normalizePositiveFinite(
            facts.outsideFeet
          );

        if (
          insideFeet !== null &&
          outsideFeet !== null
        ) {
          boundaryFeet =
            insideFeet + outsideFeet;
        }
      } else {
        return {
          status: STATUS.INVALID
        };
      }

      if (boundaryFeet === null) {
        return {
          status: STATUS.INVALID
        };
      }

      return {
        status: STATUS.VALID,
        boundaryBasis,
        boundaryFeet
      };
    }

    if (
      measurementMethod === 'triangle' ||
      measurementMethod === 'known-square-footage'
    ) {
      return {
        status: STATUS.INVALID,
        requiresManualBoundary: true
      };
    }

    return {
      status: STATUS.INVALID
    };
  }

  function deriveEdgeRequirement(
    boundaryFeet,
    multiplier
  ) {
    const normalizedBoundaryFeet =
      normalizePositiveFinite(
        boundaryFeet
      );

    const normalizedMultiplier =
      normalizePositiveFinite(
        multiplier
      );

    if (
      normalizedBoundaryFeet === null ||
      normalizedMultiplier === null ||
      !Number.isInteger(normalizedMultiplier)
    ) {
      return {
        status: STATUS.INVALID
      };
    }

    const exactRequiredFeet =
      normalizedBoundaryFeet *
      normalizedMultiplier;

    return {
      status: STATUS.VALID,
      boundaryFeet:
        normalizedBoundaryFeet,
      multiplier:
        normalizedMultiplier,
      exactRequiredFeet
    };
  }

  function deriveProjectEdgePurchase(
    exactRequiredFeet,
    stickLengthFeet
  ) {
    const normalizedRequiredFeet =
      normalizePositiveFinite(
        exactRequiredFeet
      );

    const normalizedStickLength =
      normalizePositiveFinite(
        stickLengthFeet
      );

    if (
      normalizedRequiredFeet === null ||
      normalizedStickLength === null
    ) {
      return {
        status: STATUS.INVALID
      };
    }

    const stickQuantity =
      ceilPurchaseQuantity(
        normalizedRequiredFeet /
        normalizedStickLength
      );

    return {
      status: STATUS.VALID,
      exactRequiredFeet:
        normalizedRequiredFeet,
      stickLengthFeet:
        normalizedStickLength,
      stickQuantity,
      purchasedFeet:
        stickQuantity *
        normalizedStickLength,
      includedStraightConnectorQuantity:
        stickQuantity
    };
  }

  function deriveConnectorRequirement(
    quantityPerArea,
    multiplier
  ) {
    const normalizedQuantity =
      normalizeNonnegativeWholeNumber(
        quantityPerArea
      );

    const normalizedMultiplier =
      normalizePositiveFinite(
        multiplier
      );

    if (
      normalizedQuantity.status !==
      STATUS.VALID
    ) {
      return {
        status: normalizedQuantity.status
      };
    }

    if (
      normalizedMultiplier === null ||
      !Number.isInteger(normalizedMultiplier)
    ) {
      return {
        status: STATUS.INVALID
      };
    }

    return {
      status: STATUS.VALID,
      quantityPerArea:
        normalizedQuantity.value,
      multiplier:
        normalizedMultiplier,
      totalQuantity:
        normalizedQuantity.value *
        normalizedMultiplier
    };
  }

  function sumValidNumericContributions(
    contributions,
    valueKey
  ) {
    if (!Array.isArray(contributions)) {
      return {
        status: STATUS.INVALID
      };
    }

    let total = 0;
    let validCount = 0;

    contributions.forEach(function (contribution) {
      if (
        !contribution ||
        contribution.status !== STATUS.VALID
      ) {
        return;
      }

      const value =
        normalizePositiveFinite(
          contribution[valueKey]
        );

      if (value === null) {
        return;
      }

      total += value;
      validCount += 1;
    });

    if (validCount === 0) {
      return {
        status: STATUS.UNAVAILABLE
      };
    }

    return {
      status: STATUS.VALID,
      total,
      validCount
    };
  }

  function deriveProjectFabricPurchase(
    fabricContributions
  ) {
    const aggregate =
      sumValidNumericContributions(
        fabricContributions,
        'exactLinearFeet'
      );

    if (aggregate.status !== STATUS.VALID) {
      return aggregate;
    }

    return {
      status: STATUS.VALID,
      exactLinearFeet: aggregate.total,
      linearFeet:
        Math.ceil(aggregate.total),
      contributingAreaCount:
        aggregate.validCount
    };
  }

  function deriveProjectStaplePurchase(
    stapleContributions
  ) {
    const aggregate =
      sumValidNumericContributions(
        stapleContributions,
        'exactStapleQuantity'
      );

    if (aggregate.status !== STATUS.VALID) {
      return aggregate;
    }

    return {
      status: STATUS.VALID,
      exactStapleQuantity:
        aggregate.total,
      stapleQuantity:
        Math.ceil(aggregate.total),
      contributingAreaCount:
        aggregate.validCount
    };
  }

  function deriveProjectConnectorQuantity(
    connectorContributions
  ) {
    if (!Array.isArray(connectorContributions)) {
      return {
        status: STATUS.INVALID
      };
    }

    let totalQuantity = 0;
    let validCount = 0;

    connectorContributions.forEach(
      function (contribution) {
        if (
          !contribution ||
          contribution.status !== STATUS.VALID
        ) {
          return;
        }

        const quantity =
          Number(contribution.totalQuantity);

        if (
          !Number.isFinite(quantity) ||
          quantity < 0 ||
          !Number.isInteger(quantity)
        ) {
          return;
        }

        totalQuantity += quantity;
        validCount += 1;
      }
    );

    if (validCount === 0) {
      return {
        status: STATUS.UNAVAILABLE
      };
    }

    return {
      status: STATUS.VALID,
      totalQuantity,
      contributingAreaCount:
        validCount
    };
  }

  function validateMaterialCollection(materials) {
    if (!Array.isArray(materials)) {
      return {
        status: STATUS.INVALID,
        errors: [
          {
            code: 'materials-array'
          }
        ]
      };
    }

    if (materials.length === 0) {
      return {
        status: STATUS.UNAVAILABLE,
        errors: [
          {
            code: 'materials-empty'
          }
        ]
      };
    }

    const errors = [];
    const seenIds = new Set();

    materials.forEach(function (material, index) {
      const validation =
        validateMaterialConfig(material);

      if (validation.status !== STATUS.VALID) {
        errors.push({
          index,
          code: 'material-invalid',
          materialId:
            material &&
            typeof material === 'object'
              ? material.id
              : undefined,
          fields: validation.errors
        });
      }

      if (
        material &&
        typeof material === 'object' &&
        isNonemptyString(material.id)
      ) {
        if (seenIds.has(material.id)) {
          errors.push({
            index,
            code: 'duplicate-material-id',
            materialId: material.id
          });
        } else {
          seenIds.add(material.id);
        }
      }
    });

    if (errors.length > 0) {
      return {
        status: STATUS.INVALID,
        errors
      };
    }

    return {
      status: STATUS.VALID,
      materialCount: materials.length
    };
  }

  function evaluateMaterialSelectionEligibility(material) {
    if (
      !material ||
      typeof material !== 'object' ||
      Array.isArray(material)
    ) {
      return {
        status: STATUS.INVALID,
        selectable: false,
        reason: 'invalid-material'
      };
    }

    const validation =
      validateMaterialConfig(material);

    if (validation.status !== STATUS.VALID) {
      return {
        status: STATUS.INVALID,
        selectable: false,
        reason: 'invalid-material-config'
      };
    }

    if (material.plannerVisible !== true) {
      return {
        status: STATUS.VALID,
        selectable: false,
        reason: 'not-planner-visible'
      };
    }

    if (
      material.availability ===
      MATERIAL_AVAILABILITY.TEMPORARILY_UNAVAILABLE
    ) {
      return {
        status: STATUS.VALID,
        selectable: true,
        reason: 'temporarily-unavailable'
      };
    }

    if (
      material.availability ===
      MATERIAL_AVAILABILITY.NOT_OFFERED_IN_PLANNER
    ) {
      return {
        status: STATUS.VALID,
        selectable: false,
        reason: 'not-offered-in-planner'
      };
    }

    return {
      status: STATUS.VALID,
      selectable: true,
      availability: material.availability
    };
  }

  function roundPoundsForDisplay(pounds) {
    const normalized =
      normalizePositiveFinite(pounds);

    if (normalized === null) {
      return null;
    }

    return Math.round(normalized / 10) * 10;
  }

  function formatTonsForDisplay(pounds) {
    const normalized =
      normalizePositiveFinite(pounds);

    if (
      normalized === null ||
      normalized < 2000
    ) {
      return null;
    }

    return (normalized / 2000).toFixed(2);
  }


  // SMALLER_PARTS_FUNCTIONAL_V1
  const SMALLER_PART_METHODS = Object.freeze([
    'rectangle',
    'round-area',
    'triangle'
  ]);

  function deriveSmallerPartsMeasurement(
    parts,
    deriveMeasurement
  ) {
    if (
      !Array.isArray(parts) ||
      parts.length === 0 ||
      typeof deriveMeasurement !== 'function'
    ) {
      return {
        status: STATUS.UNAVAILABLE,
        squareFeet: null,
        perimeterFacts: {},
        partMeasurements: []
      };
    }

    let totalSquareFeet = 0;
    const partMeasurements = [];

    for (const part of parts) {
      if (
        !part ||
        typeof part !== 'object' ||
        !SMALLER_PART_METHODS.includes(
          part.measurementMethod
        )
      ) {
        return {
          status: STATUS.INVALID,
          squareFeet: null,
          perimeterFacts: {},
          partMeasurements
        };
      }

      const byMethod =
        part.measurementInputsByMethod &&
        typeof part.measurementInputsByMethod === 'object'
          ? part.measurementInputsByMethod
          : {};

      const methodInputs =
        byMethod[part.measurementMethod] &&
        typeof byMethod[part.measurementMethod] === 'object'
          ? byMethod[part.measurementMethod]
          : {};

      const measurement =
        deriveMeasurement(
          part.measurementMethod,
          methodInputs
        );

      partMeasurements.push({
        id: part.id,
        measurementMethod:
          part.measurementMethod,
        measurement
      });

      if (
        !measurement ||
        measurement.status === STATUS.INVALID
      ) {
        return {
          status: STATUS.INVALID,
          squareFeet: null,
          perimeterFacts: {},
          partMeasurements
        };
      }

      if (measurement.status !== STATUS.VALID) {
        return {
          status: STATUS.UNAVAILABLE,
          squareFeet: null,
          perimeterFacts: {},
          partMeasurements
        };
      }

      if (
        !Number.isFinite(measurement.squareFeet) ||
        measurement.squareFeet <= 0
      ) {
        return {
          status: STATUS.INVALID,
          squareFeet: null,
          perimeterFacts: {},
          partMeasurements
        };
      }

      /*
       * Deliberately sum the raw deterministic square-footage
       * values. No intermediate rounding, overlap correction,
       * waste factor, allowance, GIS, polygon, or freehand rule.
       */
      totalSquareFeet += measurement.squareFeet;
    }

    return {
      status: STATUS.VALID,
      squareFeet: totalSquareFeet,
      perimeterFacts: {},
      partMeasurements,
      composition: {
        type: 'smaller-parts',
        partCount: parts.length
      }
    };
  }

  return Object.freeze({
    STATUS,
    MATERIAL_AVAILABILITY,
    purchaseMethods,
    resolvePurchaseMethod,
    deriveSpacedStaples,
    derivePartialRectangle,
    deriveAdditionalMaterials,
    knownEdgingPerimeter,
    isAuthorizedMaterialAvailability,
    validateMaterialConfig,
    validateMaterialCollection,
    evaluateMaterialSelectionEligibility,
    normalizeWeightConfig,
    deriveEstimatedWeight,
    normalizeBagConfig,
    deriveBagQuantity,
    normalizeNonnegativeWholeNumber,
    deriveFabricRequirement,
    normalizePositiveWholeNumber,
    deriveStapleRequirement,
    resolveEdgeBoundary,
    deriveEdgeRequirement,
    deriveProjectEdgePurchase,
    deriveConnectorRequirement,
    SMALLER_PART_METHODS,
    deriveSmallerPartsMeasurement,
    sumValidNumericContributions,
    deriveProjectFabricPurchase,
    deriveProjectStaplePurchase,
    deriveProjectConnectorQuantity,
    roundPoundsForDisplay,
    formatTonsForDisplay
  });
});
